import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Tabs, Table, Text, Badge, Button, Group, Title, Box, Loader, Center,
  Card, Avatar, Stack, Anchor, ActionIcon, Tooltip, Divider, ScrollArea, Paper
} from '@mantine/core';
import {
  IconUsers, IconSeeding, IconPackage, IconExternalLink,
  IconEdit, IconPlus, IconArrowLeft, IconRefresh,
  IconChevronDown, IconChevronUp
} from '@tabler/icons-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFieldTimestamp, groupByYear, UNDATED_LABEL } from '../utils/fieldDates';
import { BASE_URL } from '../Redux/apiConfig';
import { saveProperties, saveDraftProperties } from '../Redux/properties/PropertiesSlice';
import CreatePropertiesModal from './CreatePropertiesModal';
import MyFarm from './MyFarm';
import MyPersonalSpace from './MyPersonalSpace';

export default function MyNetwork() {
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const authToken = localStorage.getItem('authToken');
  const dispatch = useDispatch();

  // ── Data ─────────────────────────────────────────────────────────────────
  const [profiles, setProfiles] = useState([]);
  const [farmFields, setFarmFields] = useState([]);
  const [productLots, setProductLots] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Navigation inside this component ─────────────────────────────────────
  // null → overview, { type: 'farm', accountId, userName } → show MyFarm
  const [drilldown, setDrilldown] = useState(null);

  // ── Product lot modal ─────────────────────────────────────────────────────
  const [lotModal, setLotModal] = useState(false);
  const [lotOwner, setLotOwner] = useState(null); // { accountId, userId, userName }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
    apikey: 'abc123',
  };

  const fetchAll = useCallback(async () => {
    if (!userinfo?.accountId) return;
    setLoading(true);
    try {
      const [pRes, fRes, lRes] = await Promise.all([
        fetch(`${BASE_URL}/api/managed/profiles?managerAccountId=${userinfo.accountId}`, { headers }),
        fetch(`${BASE_URL}/api/managed/farmfields?managerAccountId=${userinfo.accountId}`, { headers }),
        fetch(`${BASE_URL}/api/managed/productlots?managerAccountId=${userinfo.accountId}`, { headers }),
      ]);
      if (pRes.ok) { const d = await pRes.json(); setProfiles(d.result || []); }
      if (fRes.ok) { const d = await fRes.json(); setFarmFields(d.result || []); }
      if (lRes.ok) { const d = await lRes.json(); setProductLots(d.result || []); }
    } finally {
      setLoading(false);
    }
  }, [userinfo?.accountId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Lot modal handlers ─────────────────────────────────────────────────────
  const openLotModal = (owner) => {
    setLotOwner(owner);
    setLotModal(true);
  };

  const onSaveLot = (obj, cb) => {
    dispatch(saveProperties({ payload: obj, cb }));
  };
  const onDraftSaveLot = (obj, cb) => {
    dispatch(saveDraftProperties({ payload: obj, cb }));
  };

  // ── Farm fields grouped by upload year ────────────────────────────────────
  const intl = useIntl();
  const [openYears, setOpenYears] = useState({});

  const fieldGroups = React.useMemo(
    () => groupByYear(farmFields, getFieldTimestamp),
    [farmFields]
  );

  // Newest year open by default, older years collapsed.
  const newestYear = fieldGroups[0]?.year;
  const isYearOpen = (year) => openYears[String(year)] ?? year === newestYear;
  const toggleYear = (year) => {
    setOpenYears((prev) => ({ ...prev, [String(year)]: !(prev[String(year)] ?? year === newestYear) }));
  };

  const formatFieldDate = (field) => {
    const time = getFieldTimestamp(field);
    if (!time) return UNDATED_LABEL;
    return intl.formatDate(time, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  // Year headers followed by their (visible) fields, kept in one table so the
  // columns line up across sections.
  const fieldRows = [];
  fieldGroups.forEach((group) => {
    fieldRows.push({ type: 'year', group });
    if (isYearOpen(group.year)) {
      group.items.forEach((field) => fieldRows.push({ type: 'field', field }));
    }
  });

  // Collect unique managed users for "Add lot" buttons
  const managedUsers = React.useMemo(() => {
    const map = {};
    profiles.forEach(p => {
      if (!map[p.accountId]) map[p.accountId] = { accountId: p.accountId, userId: p.email, userName: p.userName };
    });
    farmFields.forEach(f => {
      if (!map[f.ownerAccountId]) map[f.ownerAccountId] = { accountId: f.ownerAccountId, userId: f.email, userName: f.userName };
    });
    productLots.forEach(l => {
      if (!map[l.ownerAccountId]) map[l.ownerAccountId] = { accountId: l.ownerAccountId, userId: l.email, userName: l.userName };
    });
    return Object.values(map);
  }, [profiles, farmFields, productLots]);

  // ── Drilldown: show MyFarm for a managed user ──────────────────────────────
  if (drilldown?.type === 'profile') {
    return (
      <Box p="md">
        <Group mb="md">
          <ActionIcon variant="subtle" onClick={() => setDrilldown(null)}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={4}>
            <FormattedMessage id="mynetwork.editprofile.title" defaultMessage="Edit Profile" /> — {drilldown.userName || drilldown.accountId}
          </Title>
        </Group>
        <MyPersonalSpace accountIdOverride={drilldown.accountId} activeTab="profilePresentation" />
      </Box>
    );
  }

  if (drilldown?.type === 'farm') {
    return (
      <Box p="md">
        <Group mb="md">
          <ActionIcon variant="subtle" onClick={() => setDrilldown(null)}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={4}>
            <FormattedMessage id="mynetwork.farm.title" defaultMessage="Farm" /> — {drilldown.userName || drilldown.accountId}
          </Title>
        </Group>
        <MyFarm accountIdOverride={drilldown.accountId} />
      </Box>
    );
  }

  // ── Overview ───────────────────────────────────────────────────────────────
  return (
    <Box p="md">
      <Group position="apart" mb="md">
        <Title order={3}>
          <FormattedMessage id="mynetwork.title" defaultMessage="My Network" />
        </Title>
        <Tooltip label={<FormattedMessage id="button.refresh" defaultMessage="Refresh" />}>
          <ActionIcon variant="subtle" onClick={fetchAll} loading={loading}>
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {loading && profiles.length === 0 && farmFields.length === 0 && productLots.length === 0 && (
        <Center mt="xl"><Loader /></Center>
      )}

      <Tabs defaultValue="profiles">
        <Tabs.List>
          <Tabs.Tab value="profiles" icon={<IconUsers size={16} />}>
            <FormattedMessage id="mynetwork.tab.profiles" defaultMessage="Profiles" />
            {profiles.length > 0 && <Badge ml={6} size="xs" variant="filled">{profiles.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="farmfields" icon={<IconSeeding size={16} />}>
            <FormattedMessage id="mynetwork.tab.farmfields" defaultMessage="Farm Fields" />
            {farmFields.length > 0 && <Badge ml={6} size="xs" variant="filled">{farmFields.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="productlots" icon={<IconPackage size={16} />}>
            <FormattedMessage id="mynetwork.tab.lots" defaultMessage="Product Lots" />
            {productLots.length > 0 && <Badge ml={6} size="xs" variant="filled">{productLots.length}</Badge>}
          </Tabs.Tab>
        </Tabs.List>

        {/* ── PROFILES ── */}
        <Tabs.Panel value="profiles" pt="md">
          {profiles.length === 0 && !loading && (
            <Text color="dimmed" align="center" mt="xl">
              <FormattedMessage id="mynetwork.empty.profiles" defaultMessage="No profiles found." />
            </Text>
          )}
          <Group spacing="md" align="flex-start">
            {profiles.map((p) => (
              <Card key={p.accountId} shadow="sm" padding="md" radius="md" withBorder style={{ width: 240 }}>
                <Stack spacing="xs" align="center">
                  <Avatar
                    src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${p.accountId}/hero.jpg`}
                    size={64}
                    radius="xl"
                  >
                    {(p.userName || p.email || '?')[0].toUpperCase()}
                  </Avatar>
                  <Text weight={600} size="sm" align="center">{p.userName || p.email}</Text>
                  <Text color="dimmed" size="xs" align="center">{p.email}</Text>
                  <Text size="xs" color="dimmed">{p.accountId}</Text>
                  {p.profile?.title && <Text size="sm" align="center">{p.profile.title}</Text>}
                  {p.profile?.subtitle && <Text size="xs" color="dimmed" align="center">{p.profile.subtitle}</Text>}
                  <Anchor href={`/profile/${p.accountId}`} target="_blank" size="xs">
                    <Group spacing={4}>
                      <FormattedMessage id="mynetwork.viewprofile" defaultMessage="View Profile" />
                      <IconExternalLink size={12} />
                    </Group>
                  </Anchor>
                  <Button
                    size="xs"
                    variant="light"
                    leftIcon={<IconEdit size={14} />}
                    onClick={() => setDrilldown({ type: 'profile', accountId: p.accountId, userName: p.userName || p.email })}
                  >
                    <FormattedMessage id="mynetwork.editprofile" defaultMessage="Edit Profile" />
                  </Button>
                </Stack>
              </Card>
            ))}
          </Group>
        </Tabs.Panel>

        {/* ── FARM FIELDS ── */}
        <Tabs.Panel value="farmfields" pt="md">
          <Group position="apart" mb="sm">
            <Text size="sm" color="dimmed">
              <FormattedMessage id="mynetwork.farmfields.hint" defaultMessage="Click 'Manage Farm' to add or edit fields for a user." />
            </Text>
            <Group spacing="xs">
              {managedUsers.map(u => (
                <Button
                  key={u.accountId}
                  size="xs"
                  leftIcon={<IconEdit size={14} />}
                  variant="light"
                  onClick={() => setDrilldown({ type: 'farm', accountId: u.accountId, userName: u.userName || u.userId })}
                >
                  <FormattedMessage id="mynetwork.managefarm" defaultMessage="Manage Farm" /> — {u.userName || u.userId}
                </Button>
              ))}
            </Group>
          </Group>
          {farmFields.length === 0 && !loading && (
            <Text color="dimmed" align="center" mt="xl">
              <FormattedMessage id="mynetwork.empty.farmfields" defaultMessage="No farm fields found." />
            </Text>
          )}
          <ScrollArea>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th><FormattedMessage id="mynetwork.col.owner" defaultMessage="Owner" /></th>
                  <th><FormattedMessage id="mynetwork.col.email" defaultMessage="Email" /></th>
                  <th><FormattedMessage id="mynetwork.col.fieldname" defaultMessage="Field Name" /></th>
                  <th><FormattedMessage id="mynetwork.col.product" defaultMessage="Product" /></th>
                  <th><FormattedMessage id="mynetwork.col.region" defaultMessage="Region" /></th>
                  <th style={{ width: 130 }}><FormattedMessage id="myfarm.field.time" defaultMessage="Date" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fieldRows.map(({ type, group, field: f }, i) => type === 'year' ? (
                  <tr
                    key={`year-${group.label}`}
                    onClick={() => toggleYear(group.year)}
                    style={{ cursor: 'pointer', backgroundColor: '#f1f3f5' }}
                  >
                    <td colSpan={7}>
                      <Group spacing={8}>
                        {isYearOpen(group.year)
                          ? <IconChevronUp size={16} />
                          : <IconChevronDown size={16} />}
                        <Text size="sm" weight={600}>{group.label}</Text>
                        <Badge size="sm" color="gray" variant="light">{group.items.length}</Badge>
                      </Group>
                    </td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td><Text size="sm" weight={500}>{f.userName || f.ownerAccountId}</Text></td>
                    <td><Text size="sm" color="dimmed">{f.email}</Text></td>
                    <td><Text size="sm">{f.fieldName || f.name || '—'}</Text></td>
                    <td><Text size="sm">{f.product || '—'}</Text></td>
                    <td><Text size="sm">{f.region || '—'}</Text></td>
                    <td><Text size="sm" color="dimmed" style={{ whiteSpace: 'nowrap' }}>{formatFieldDate(f)}</Text></td>
                    <td>
                      <Tooltip label={<FormattedMessage id="mynetwork.managefarm" defaultMessage="Manage Farm" />}>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={() => setDrilldown({ type: 'farm', accountId: f.ownerAccountId, userName: f.userName || f.email })}
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>

        {/* ── PRODUCT LOTS ── */}
        <Tabs.Panel value="productlots" pt="md">
          <Group position="apart" mb="sm">
            <Text size="sm" color="dimmed">
              <FormattedMessage id="mynetwork.lots.hint" defaultMessage="Add a new lot on behalf of a managed user." />
            </Text>
            <Group spacing="xs">
              {managedUsers.map(u => (
                <Button
                  key={u.accountId}
                  size="xs"
                  leftIcon={<IconPlus size={14} />}
                  variant="light"
                  onClick={() => openLotModal(u)}
                >
                  <FormattedMessage id="mynetwork.addlot" defaultMessage="Add Lot" /> — {u.userName || u.userId}
                </Button>
              ))}
            </Group>
          </Group>
          {productLots.length === 0 && !loading && (
            <Text color="dimmed" align="center" mt="xl">
              <FormattedMessage id="mynetwork.empty.lots" defaultMessage="No product lots found." />
            </Text>
          )}
          <ScrollArea>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th><FormattedMessage id="mynetwork.col.owner" defaultMessage="Owner" /></th>
                  <th><FormattedMessage id="mynetwork.col.email" defaultMessage="Email" /></th>
                  <th><FormattedMessage id="mynetwork.col.product" defaultMessage="Product" /></th>
                  <th><FormattedMessage id="mynetwork.col.quantity" defaultMessage="Quantity" /></th>
                  <th><FormattedMessage id="mynetwork.col.period" defaultMessage="Period" /></th>
                  <th><FormattedMessage id="mynetwork.col.status" defaultMessage="Status" /></th>
                  <th><FormattedMessage id="mynetwork.col.sku" defaultMessage="SKU" /></th>
                </tr>
              </thead>
              <tbody>
                {productLots.map((l, i) => (
                  <tr key={i}>
                    <td><Text size="sm" weight={500}>{l.userName || l.ownerAccountId}</Text></td>
                    <td><Text size="sm" color="dimmed">{l.email}</Text></td>
                    <td><Text size="sm">{l.ProductName || '—'}</Text></td>
                    <td><Text size="sm">{l.Quantity} {l.UOM}</Text></td>
                    <td><Text size="sm">{l.Period || '—'}</Text></td>
                    <td>
                      <Badge size="sm" color={l.PublishId === 2 ? 'gray' : l.PublishId === 0 ? 'yellow' : 'green'}>
                        {l.PublishId === 2 ? 'Draft' : l.PublishId === 0 ? 'Pending' : 'Published'}
                      </Badge>
                    </td>
                    <td>
                      {l.Sku ? (
                        <Text size="xs" color="dimmed" style={{ fontFamily: 'monospace' }}>
                          {String(l.Sku).slice(0, 12)}…
                        </Text>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>

      {/* ── Add Lot Modal ─────────────────────────────────────────────────── */}
      {lotOwner && (
        <CreatePropertiesModal
          modal={lotModal}
          toggle={() => setLotModal(false)}
          onSave={onSaveLot}
          onDraftSave={onDraftSaveLot}
          ownerOverride={{ accountId: lotOwner.accountId, userId: lotOwner.userId }}
        />
      )}
    </Box>
  );
}
